import { useState, useEffect, useMemo } from 'react';
import { validateIndicatorSecurity } from '@/services/indicator-runner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBolt,
  faCalendarDays,
  faChartBar,
  faFileLines,
  faPenToSquare,
  faSliders,
  faUserCheck,
} from '@fortawesome/free-solid-svg-icons';

const mkIcon = (def: import('@fortawesome/fontawesome-svg-core').IconDefinition) =>
  ({ className = '' }: { className?: string }) =>
    <FontAwesomeIcon icon={def} className={className} />;

const SEVERITY_COLORS = {
  good:     { bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', text: 'text-emerald-700 dark:text-emerald-400', dot: 'bg-emerald-400' },
  warning:  { bg: 'bg-amber-500/10',   border: 'border-amber-500/20',   text: 'text-amber-700 dark:text-amber-400',   dot: 'bg-amber-400'   },
  critical: { bg: 'bg-red-500/10',     border: 'border-red-500/20',     text: 'text-red-700 dark:text-red-400',       dot: 'bg-red-400'     },
};

const STORAGE_KEY = 'notion_indicators_custom';

export interface NotionActivityData {
  events: NotionEventItem[];
  authors: { id: string; name: string; count: number }[];
  dailyCounts: { date: string; created: number; edited: number }[];
  totalPages: number;
  uniqueContributors: number;
  dateRange: { start: string; end: string };
}

export interface NotionEventItem {
  id: string;
  title: string;
  url: string;
  isNew: boolean;
  createdTime: string;
  lastEditedTime: string;
  createdById: string;
  createdByName: string;
  editedById: string;
  editedByName: string;
  parentType?: string;
  timestamp: string;
}

export interface NotionIndicatorDef {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  code?: string;
}

export interface NotionIndicatorResult {
  id: string;
  name: string;
  description: string;
  value: number;
  label: string;
  severity: 'good' | 'warning' | 'critical';
  detail?: string;
  icon?: any;
  baseline?: string;
}

function loadCustomDefs(): Record<string, Partial<NotionIndicatorDef>> {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}'); } catch { return {}; }
}

function saveCustomDefs(defs: Record<string, Partial<NotionIndicatorDef>>) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(defs));
}

function computeBuiltIn(data: NotionActivityData, lookbackDays: number): NotionIndicatorResult[] {
  const { events } = data;
  if (!events.length) return [];

  const pagesPerDay = +(events.length / Math.max(lookbackDays, 1)).toFixed(2);
  const ppdSeverity: NotionIndicatorResult['severity'] = pagesPerDay >= 2 ? 'good' : pagesPerDay >= 0.5 ? 'warning' : 'critical';

  const newPages = events.filter(e => e.isNew).length;
  const newRatio = Math.round((newPages / events.length) * 100);
  const newSeverity: NotionIndicatorResult['severity'] = newRatio >= 20 ? 'good' : newRatio >= 10 ? 'warning' : 'critical';

  const topAuthorCount = data.authors[0]?.count ?? 0;
  const concRatio = Math.round((topAuthorCount / events.length) * 100);
  const concSeverity: NotionIndicatorResult['severity'] = concRatio <= 40 ? 'good' : concRatio <= 70 ? 'warning' : 'critical';

  const activeDays = new Set(events.map(e => e.timestamp.slice(0, 10))).size;
  const activePct = Math.round((activeDays / Math.max(lookbackDays, 1)) * 100);
  const activeSeverity: NotionIndicatorResult['severity'] = activePct >= 50 ? 'good' : activePct >= 25 ? 'warning' : 'critical';

  const uniqueEditors = new Set(events.map(e => e.editedById)).size;
  const editorSeverity: NotionIndicatorResult['severity'] = uniqueEditors >= 3 ? 'good' : uniqueEditors >= 2 ? 'warning' : 'critical';

  const dbPages = events.filter(e => e.parentType === 'database_id').length;
  const dbRatio = Math.round((dbPages / events.length) * 100);
  const dbSeverity: NotionIndicatorResult['severity'] = dbRatio >= 30 ? 'good' : dbRatio >= 10 ? 'warning' : 'critical';

  return [
    {
      id: 'pages-per-day', name: 'Pages/Day', description: 'Average pages edited per calendar day',
      value: pagesPerDay, label: `${pagesPerDay}`, severity: ppdSeverity,
      detail: `${events.length} pages over ${lookbackDays}d`,
      icon: mkIcon(faFileLines), baseline: '✓ ≥2 | ⚠ 0.5–2 | ✗ <0.5',
    },
    {
      id: 'new-pages-ratio', name: 'New Pages', description: 'Percentage of activity that is newly created pages',
      value: newRatio, label: `${newRatio}%`, severity: newSeverity,
      detail: `${newPages} new, ${events.length - newPages} edits`,
      icon: mkIcon(faFileLines), baseline: '✓ ≥20% | ⚠ 10–20% | ✗ <10%',
    },
    {
      id: 'contributor-concentration', name: 'Contributor Mix', description: 'Activity share of top contributor — lower is healthier',
      value: concRatio, label: `${concRatio}%`, severity: concSeverity,
      detail: data.authors[0] ? `${data.authors[0].name}: ${topAuthorCount} pages` : 'n/a',
      icon: mkIcon(faUserCheck), baseline: '✓ ≤40% | ⚠ 40–70% | ✗ >70%',
    },
    {
      id: 'active-days', name: 'Active Days', description: 'Percentage of days with any Notion activity',
      value: activePct, label: `${activePct}%`, severity: activeSeverity,
      detail: `${activeDays} of ${lookbackDays} days had activity`,
      icon: mkIcon(faCalendarDays), baseline: '✓ ≥50% | ⚠ 25–50% | ✗ <25%',
    },
    {
      id: 'unique-editors', name: 'Unique Editors', description: 'Number of distinct people editing pages',
      value: uniqueEditors, label: `${uniqueEditors}`, severity: editorSeverity,
      detail: `${data.uniqueContributors} contributors total`,
      icon: mkIcon(faPenToSquare), baseline: '✓ ≥3 | ⚠ 2 | ✗ 1',
    },
    {
      id: 'database-usage', name: 'Database Pages', description: 'Pages inside databases — structured content ratio',
      value: dbRatio, label: `${dbRatio}%`, severity: dbSeverity,
      detail: `${dbPages} of ${events.length} pages in databases`,
      icon: mkIcon(faChartBar), baseline: '✓ ≥30% | ⚠ 10–30% | ✗ <10%',
    },
  ];
}

interface NotionIndicatorsProps {
  data: NotionActivityData;
  lookbackDays: number;
  triggerCalculate: number;
}

export function NotionIndicators({ data, triggerCalculate, lookbackDays }: NotionIndicatorsProps) {
  const [customDefs, setCustomDefs] = useState<Record<string, Partial<NotionIndicatorDef>>>(() => loadCustomDefs());
  const [showCustomize, setShowCustomize] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [customCode, setCustomCode] = useState('');

  const results = useMemo(() => computeBuiltIn(data, lookbackDays), [data, lookbackDays, triggerCalculate]);
  const enabledResults = useMemo(() => results.filter(r => customDefs[r.id]?.enabled !== false), [results, customDefs]);

  const goodCount = enabledResults.filter(r => r.severity === 'good').length;
  const warningCount = enabledResults.filter(r => r.severity === 'warning').length;
  const criticalCount = enabledResults.filter(r => r.severity === 'critical').length;

  useEffect(() => { setCustomDefs(loadCustomDefs()); }, [triggerCalculate]);

  return (
    <div className="p-4 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faChartBar} className="h-4 w-4 text-gray-700 dark:text-gray-300" />
          <span className="text-sm font-semibold text-gray-900 dark:text-white">Notion Indicators</span>
          <div className="flex items-center gap-1">
            {goodCount > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/20">{goodCount} ✓</span>}
            {warningCount > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/20">{warningCount} ⚠</span>}
            {criticalCount > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-700 dark:text-red-300 border border-red-500/20">{criticalCount} ✗</span>}
          </div>
        </div>
        <button
          onClick={() => setShowCustomize(!showCustomize)}
          className="text-xs px-2 py-1 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600 rounded transition-colors flex items-center gap-1"
        >
          <FontAwesomeIcon icon={faSliders} className="h-3 w-3" />
          {showCustomize ? 'Hide' : 'Customise'}
        </button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-slate-700/30">
        <table className="w-full text-[11px]">
          <thead>
            <tr className="bg-gray-50 dark:bg-slate-900/50">
              <th className="text-left px-3 py-2 text-[10px] font-semibold text-gray-500 dark:text-slate-400">Indicator</th>
              <th className="text-center px-2 py-2 text-[10px] font-semibold text-gray-500 dark:text-slate-400">Value</th>
              <th className="text-left px-3 py-2 text-[10px] font-semibold text-gray-500 dark:text-slate-400">Baseline</th>
            </tr>
          </thead>
          <tbody>
            {enabledResults.map(r => {
              const Icon = r.icon || mkIcon(faChartBar);
              const colors = SEVERITY_COLORS[r.severity];
              return (
                <tr key={r.id} className="border-t border-gray-100 dark:border-slate-700/15 hover:bg-gray-50 dark:hover:bg-slate-700/10">
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <Icon className="h-3 w-3 text-gray-400 dark:text-slate-500 flex-shrink-0" />
                      <div>
                        <span className="text-[11px] text-gray-700 dark:text-slate-300">{r.name}</span>
                        {r.detail && <p className="text-[9px] text-gray-500 dark:text-slate-500 mt-0.5">{r.detail}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-2 py-2 text-center">
                    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold ${colors.bg} ${colors.text} border ${colors.border}`}>
                      {r.label}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <span className="text-[9px] text-gray-500 dark:text-slate-500 font-mono">{r.baseline || '—'}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showCustomize && (
        <div className="mt-4 p-3 bg-gray-50 dark:bg-slate-900/50 rounded-lg border border-gray-200 dark:border-slate-700">
          <h4 className="text-xs font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
            <FontAwesomeIcon icon={faSliders} className="h-3 w-3" /> Customise Indicators
          </h4>
          <div className="space-y-2">
            {results.map(r => {
              const def = customDefs[r.id] || {};
              const isEditing = editingId === r.id;
              const Icon = r.icon || mkIcon(faChartBar);
              return (
                <div key={r.id} className="flex items-center justify-between p-2 bg-white dark:bg-slate-800 rounded border border-gray-200 dark:border-slate-600">
                  <div className="flex items-center gap-2">
                    <input type="checkbox" checked={def.enabled !== false}
                      onChange={e => {
                        const updated = { ...customDefs, [r.id]: { ...customDefs[r.id], enabled: e.target.checked } };
                        setCustomDefs(updated); saveCustomDefs(updated);
                      }}
                      className="h-3 w-3 rounded border-gray-300 dark:border-slate-600" />
                    <Icon className="h-3 w-3 text-gray-400 dark:text-slate-500" />
                    <span className="text-xs text-gray-700 dark:text-slate-300">{r.name}</span>
                    {def.code && <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">custom</span>}
                  </div>
                  <button onClick={() => { setEditingId(isEditing ? null : r.id); setCustomCode(def.code || ''); }}
                    className="text-xs px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors">
                    {isEditing ? 'Cancel' : 'Override'}
                  </button>
                </div>
              );
            })}
          </div>
          {editingId && (
            <div className="mt-3 space-y-2">
              <label className="text-xs font-medium text-gray-700 dark:text-slate-300 flex items-center gap-1">
                <FontAwesomeIcon icon={faFileLines} className="h-3 w-3" />
                Custom JavaScript for {results.find(r => r.id === editingId)?.name}:
              </label>
              <textarea value={customCode} onChange={e => setCustomCode(e.target.value)}
                placeholder={`// Return { value: number, label: string, severity: 'good'|'warning'|'critical' }\n// Available: data (NotionActivityData), lookbackDays (number)`}
                className="w-full h-24 p-2 text-xs font-mono bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500" />
              <div className="flex gap-2">
                <button onClick={() => {
                  try {
                    const validation = validateIndicatorSecurity(customCode);
                    if (!validation.safe) { alert('Security validation failed: ' + validation.warnings.join(', ')); return; }
                    const fn = new Function('data', 'lookbackDays', customCode);
                    const test = fn(data, lookbackDays);
                    if (!test || typeof test.value !== 'number' || !test.label || !['good', 'warning', 'critical'].includes(test.severity)) {
                      alert('Invalid result. Must return { value, label, severity }.'); return;
                    }
                    const updated = { ...customDefs, [editingId]: { ...customDefs[editingId], code: customCode } };
                    setCustomDefs(updated); saveCustomDefs(updated);
                    setEditingId(null); setCustomCode('');
                  } catch (e: any) { alert('Code error: ' + e.message); }
                }} className="text-xs px-2 py-1 bg-emerald-600 hover:bg-emerald-500 text-white rounded transition-colors flex items-center gap-1">
                  <FontAwesomeIcon icon={faBolt} className="h-3 w-3" /> Save
                </button>
                <button onClick={() => {
                  const updated = { ...customDefs, [editingId]: { ...customDefs[editingId], code: undefined } };
                  setCustomDefs(updated); saveCustomDefs(updated); setEditingId(null); setCustomCode('');
                }} className="text-xs px-2 py-1 bg-gray-600 hover:bg-gray-500 text-white rounded transition-colors">Reset</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
